import { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import axios from "axios";

// Import de Context
import { useDataContext } from "../Context/dataContext";

// Import de Hooks
import { useAxiosInterceptors } from "../Hooks/useAxiosInterceptors";
import { useFetchData } from "../Hooks/useFetchData";
import { useCurrencyPrice } from "../Hooks/useCurrencyPrice";
import { usePorcentPrice } from "../Hooks/usePorcentPrice";

// Import de Navbar
import { UserNavbar } from "../Components/UserNavbar";

// Import de Iconos
import {
  FaCheckCircle,
  FaTimesCircle,
  FaEuroSign,
  FaDollarSign,
  FaPoundSign,
} from "react-icons/fa";

import { NotFound404 } from "./NotFound404";

function SendMoney() {
  // Context and hooks
  useAxiosInterceptors();
  const { url, accessToken } = useDataContext();
  const { exchangeRates } = useCurrencyPrice();
  const history = useHistory();

  //User data
  const { user, directory } = useFetchData(accessToken.access_token);

  // Verificación al entrar a la página
  const [blockByVerification, setBlockByVerification] = useState(false);
  const [verificationReady, setVerificationReady] = useState(false);

  const checkUserVerification = () => {
    const verif = user?.use_verif;
    if (!verif) return;

    setVerificationReady(true);
    setBlockByVerification(verif === "N" || verif === "E");
  };

  useEffect(() => {
    checkUserVerification();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.use_verif]);

  // Porcent Data
  const { porcents } = usePorcentPrice(accessToken.access_token);
  const [porcent, setPorcent] = useState({});
  const fetchDataPorcentId = async (id) => {
    try {
      const response = await axios.get(`${url}/PorcentPrice/${id}`, {
        headers: {
          Authorization: `Bearer ${accessToken.access_token}`,
        },
      });
      setPorcent(response.data);
    } catch (error) {
      console.log(error);
    }
  };

  // Estados Para los pasos
  const [step, setStep] = useState(1);

  // Estados para los datos del envío
  const [amount, setAmount] = useState("");
  const [payment, setPayment] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);

  // Estados para la entrega delivery
  const [deliveryOption, setDeliveryOption] = useState("");
  const [receiverName, setReceiverName] = useState("");
  const [receiverDni, setReceiverDni] = useState("");
  const [receiverPhone, setReceiverPhone] = useState("");
  const [receiverAddress, setReceiverAddress] = useState("");

  // Estados para manejo de errores y loading
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [transactionSuccess, setTransactionSuccess] = useState(false);

  if (!verificationReady) {
    return (
      <div className="step-loader">
        <div className="spinner"></div>
        <p>Cargando...</p>
      </div>
    );
  }

  if (blockByVerification) {
    return <NotFound404 />;
  }

  // Mapeo de banderas de países
  const countryFlags = {
    Venezuela: "ve",
    Argentina: "ar",
    Chile: "cl",
    Peru: "pe",
    Brasil: "br",
    Colombia: "co",
    Ecuador: "ec",
    Panamá: "pa",
    "República Dominicana": "do",
    "Estados Unidos": "us",
    Europa: "eu",
  };

  // Obtener el porcentaje según la moneda
  const getPercentage = () => {
    if (payment === "GBP") return porcent.por_porcentGbp;
    if (payment === "USD") return porcent.por_porcentUsd;
    if (payment === "EUR") return porcent.por_porcentEur;
    return 0;
  };

  // Calcular el valor total a enviar
  const calculateValue = (amount, payment, porcent) => {
    const amountFloat = parseFloat(amount);
    const deliveryPrice = parseFloat(porcent.por_deliveryPrice);

    if (payment === "EUR") {
      if (porcent.por_status === "Obligatorio") {
        return (
          amountFloat +
          amountFloat * (porcent.por_porcentEur / 100) +
          deliveryPrice
        );
      } else {
        return amountFloat + amountFloat * (porcent.por_porcentEur / 100);
      }
    } else if (payment === "GBP") {
      if (porcent.por_status === "Obligatorio") {
        return (
          amountFloat +
          amountFloat * (porcent.por_porcentGbp / 100) +
          deliveryPrice
        );
      } else {
        return amountFloat + amountFloat * (porcent.por_porcentGbp / 100);
      }
    } else if (payment === "USD") {
      if (porcent.por_status === "Obligatorio") {
        return (
          amountFloat +
          amountFloat * (porcent.por_porcentUsd / 100) +
          deliveryPrice
        );
      } else {
        return amountFloat + amountFloat * (porcent.por_porcentUsd / 100);
      }
    }
    return null;
  };

  // Manejo del cambio de moneda
  const handleCurrencyChange = (currency) => {
    setPayment(currency);
    setStep(2);
  };

  // Manejo del cambio de monto
  const handleAmountChange = (e) => {
    const input = e.target.value;
    setAmount(input);

    // Validar que sea un número válido
    const value = parseFloat(input);
    if (isNaN(value)) {
      setError("Ingresa un monto válido.");
      return;
    }

    const balances = {
      EUR: user?.use_amountEur ?? 0,
      USD: user?.use_amountUsd ?? 0,
      GBP: user?.use_amountGbp ?? 0,
    };

    if (deliveryOption === "efectivo") {
      if (value < 100) {
        setError("El monto a enviar debe ser mayor o igual a 100.");
      } else if (value % 20 !== 0) {
        setError("El monto a enviar debe ser múltiplo de 20.");
      } else {
        setError("");
      }
    } else if (selectedCountry !== "Venezuela" && value < 100) {
      setError("El monto a enviar debe ser mayor o igual a 100.");
    } else {
      if (value < 20) {
        setError("El monto a enviar debe ser mayor a 20.");
      } else if (payment && value > balances[payment]) {
        setError("El monto supera tu saldo disponible.");
      } else {
        setError("");
      }
    }
  };

  // Hacer el envío
  const handleSend = async () => {
    setLoading(true);

    const formData = new FormData();
    formData.append("mov_currency", payment);
    formData.append(
      "mov_amount",
      user.use_loyaltyEurtoBs > 0 ? user.use_loyaltyEurtoBs : amount
    );
    formData.append("mov_type", "Retiro");
    formData.append("mov_status", "E");
    formData.append("mov_code", "");
    formData.append("mov_phone", "");
    formData.append(
      "mov_oldAmount",
      deliveryOption === "efectivo"
        ? calculateValue(amount, payment, porcent)
        : amount
    );

    formData.append(
      "mov_comment",
      deliveryOption === "efectivo"
        ? `Retiro de divisa en efectivo \n  Ciudad: ` +
            porcent.por_stateLocation +
            `\n Persona que recibe: ` +
            receiverName +
            `\n DNI de quien recibe: ` +
            receiverDni +
            `\n Teléfono: ` +
            receiverPhone +
            `\n Dirección: ` +
            receiverAddress +
            `\n Monto: ` +
            amount
        : deliveryOption === "transferencia" || deliveryOption === "Zelle"
        ? `Propietario: ${selectedBeneficiary?.accbsUser_owner ?? ""}
            \n DNI: ${selectedBeneficiary?.accbsUser_dni ?? ""}
            ${
              selectedBeneficiary.accbsUser_type === "Pago Móvil"
                ? `\n Teléfono: ${selectedBeneficiary?.accbsUser_phone ?? ""}`
                : `\n Cuenta: ${selectedBeneficiary?.accbsUser_number ?? ""}`
            }
            \n Banco: ${selectedBeneficiary?.accbsUser_bank ?? ""}
            \n Monto: ${(
              parseFloat(amount) *
              (exchangeRates.find(
                (rate) =>
                  rate.country === selectedCountry &&
                  rate.oldCurrency === payment
              )?.value || 0)
            ).toLocaleString()}`
        : "-"
    );
    formData.append("mov_img", "Retiro de Divisa");
    formData.append("mov_typeOutflow", deliveryOption);
    formData.append("mov_accEurId", payment === "EUR" ? 99 : 0);
    formData.append("mov_accUsdId", payment === "USD" ? 99 : 0);
    formData.append("mov_accGbpId", payment === "GBP" ? 99 : 0);
    formData.append("mov_userId", user.use_id);
    formData.append(
      "mov_accBsUserId",
      selectedBeneficiary ? selectedBeneficiary.accbsUser_id : 0
    );
    formData.append(
      "mov_currencyPrice",
      user.use_loyaltyEurtoBs > 0
        ? user.use_loyaltyEurtoBs
        : exchangeRates.find(
            (rate) =>
              rate.country === selectedCountry && rate.oldCurrency === payment
          )?.value || 0
    );
    formData.append("mov_oldCurrency", payment);

    const formDataUser = new FormData();
    formDataUser.append(
      "use_amountUsd",
      payment === "USD" && deliveryOption === "efectivo"
        ? user.use_amountUsd - calculateValue(amount, payment, porcent)
        : payment === "USD" && deliveryOption !== "efectivo"
        ? user.use_amountUsd - amount
        : user.use_amountUsd
    );
    formDataUser.append(
      "use_amountGbp",
      payment === "GBP" && deliveryOption === "efectivo"
        ? user.use_amountGbp - calculateValue(amount, payment, porcent)
        : payment === "GBP" && deliveryOption !== "efectivo"
        ? user.use_amountGbp - amount
        : user.use_amountGbp
    );
    formDataUser.append(
      "use_amountEur",
      payment === "EUR" && deliveryOption === "efectivo"
        ? user.use_amountEur - calculateValue(amount, payment, porcent)
        : payment === "EUR" && deliveryOption !== "efectivo"
        ? user.use_amountEur - amount
        : user.use_amountEur
    );

    try {
      // Crear el movimiento
      const response = await axios.post(`${url}/Movements/create`, formData, {
        headers: {
          Authorization: `Bearer ${accessToken.access_token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      // Actualizar saldo del usuario
      await axios.put(`${url}/Users/${user.use_id}`, formDataUser, {
        headers: {
          Authorization: `Bearer ${accessToken.access_token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      // Obtener el ID del movimiento creado
      const idMovement = response.data.mov_id; // Suponiendo que el ID del movimiento viene en la respuesta

      // Enviar notificación de pago con el ID del movimiento
      const emailRecipient = "Recibirnotificacionesdepago@hotmail.com"; // Correo de destino fijo
      const emailRoleD = "Rolrecibirypagos@gmail.com";

      try {
        await axios.post(
          `${url}/Mailer/pendantTransfer/${emailRecipient}/${idMovement}`
        );
        await axios.post(
          `${url}/Mailer/pendantTransfer/${emailRoleD}/${idMovement}`
        );
        console.log("Payment notification sent successfully");
      } catch (error) {
        console.error("Error sending payment notification:", error);
      }

      setTransactionSuccess(true);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sendmoney-page">
      <div className="progress-bar-container">
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${(step / 7) * 100}%` }}
          />
        </div>
        <p>Paso {step} de 7</p>
      </div>

      {step === 1 && (
        <div className="step-content">
          <h2>Selecciona la moneda</h2>
          <div className="currency-options">
            <div
              className="currency-card"
              onClick={() => handleCurrencyChange("EUR")}
            >
              <FaEuroSign />
              <span>Euros</span>
            </div>
            <div
              className="currency-card"
              onClick={() => handleCurrencyChange("USD")}
            >
              <FaDollarSign />
              <span>Dólares</span>
            </div>
            <div
              className="currency-card"
              onClick={() => handleCurrencyChange("GBP")}
            >
              <FaPoundSign />
              <span>Libras</span>
            </div>
          </div>
        </div>
      )}

      {/* Paso 2: País */}
      {step === 2 && (
        <div className="step-content">
          <h2>Selecciona el país destino</h2>
          <div className="country-options">
            {Object.keys(countryFlags).map((country) => (
              <div
                key={country}
                className={`country-card ${
                  selectedCountry === country ? "active" : ""
                }`}
                onClick={() => {
                  setSelectedCountry(country);
                  setStep(3);
                }}
              >
                <img
                  src={`https://flagcdn.com/w40/${countryFlags[country]}.png`}
                  alt={country}
                  className="flag-icon-large"
                />
                <p>{country}</p>
              </div>
            ))}
          </div>
          <div className="actions">
            <button onClick={() => setStep(1)}>Atrás</button>
          </div>
        </div>
      )}

      {/* Paso 3: Método */}
      {step === 3 && (
        <div className="step-content">
          <h2>¿Cómo deseas entregar?</h2>
          <div className="delivery-options">
            <div
              className={`delivery-card ${
                deliveryOption === "transferencia" ? "active" : ""
              }`}
              onClick={() => {
                setDeliveryOption("transferencia");
                setStep(4);
              }}
            >
              <p>Transferencia bancaria</p>
            </div>
            {selectedCountry !== "Estados Unidos" && (
              <div
                className={`delivery-card ${
                  deliveryOption === "efectivo" ? "active" : ""
                }`}
                onClick={() => {
                  setDeliveryOption("efectivo");
                  setStep(4);
                }}
              >
                <p>Entrega en efectivo</p>
              </div>
            )}
            {selectedCountry === "Estados Unidos" && (
              <div
                className={`delivery-card ${
                  deliveryOption === "Zelle" ? "active" : ""
                }`}
                onClick={() => {
                  setDeliveryOption("Zelle");
                  setStep(4);
                }}
              >
                <p>Envío Zelle</p>
              </div>
            )}
          </div>
          <div className="actions">
            <button onClick={() => setStep(2)}>Atrás</button>
          </div>
        </div>
      )}

      {/* Paso 4: Monto */}
      {step === 4 && (
        <div className="step-content">
          <h2>Ingresa el monto a enviar</h2>
          {deliveryOption !== "efectivo" && (
            <div className="exchange-box">
              <p>
                Tasa de cambio:{" "}
                {user.use_loyaltyEurtoBs > 0
                  ? `${user.use_loyaltyEurtoBs} BS = 1 EUR`
                  : exchangeRates
                      .filter(
                        (rate) =>
                          rate.country === selectedCountry &&
                          rate.oldCurrency === payment
                      )
                      .map((rate, idx) => (
                        <span key={idx}>
                          <strong>
                            {rate.value} {rate.currency} = 1 {rate.oldCurrency}
                          </strong>
                        </span>
                      ))}
                {exchangeRates.filter(
                  (rate) => rate.country === selectedCountry
                ).length === 0 && <strong>N/A</strong>}
              </p>

              {amount && !error && (
                <p>
                  Monto en destino:{" "}
                  <strong>
                    {(
                      parseFloat(amount) *
                      (user.use_loyaltyEurtoBs > 0
                        ? user.use_loyaltyEurtoBs
                        : exchangeRates.find(
                            (rate) =>
                              rate.country === selectedCountry &&
                              rate.oldCurrency === payment
                          )?.value || 0)
                    ).toLocaleString()}
                  </strong>
                </p>
              )}
            </div>
          )}

          <div className="form-group">
            <label>Monto</label>
            <input
              type="number"
              placeholder="Ej: 100"
              value={amount}
              onChange={handleAmountChange}
              className={error ? "invalid" : ""}
            />
            <div className={`wallet-check ${error ? "error" : "success"}`}>
              {error
                ? error
                : `Saldo disponible: ${
                    payment === "EUR"
                      ? user.use_amountEur
                      : payment === "USD"
                      ? user.use_amountUsd
                      : user.use_amountGbp
                  } ${payment}`}
            </div>
          </div>

          <div className="actions">
            <button onClick={() => setStep(3)}>Atrás</button>
            <button
              disabled={
                !amount ||
                !!error ||
                (selectedCountry !== "Venezuela" && amount < 100)
              }
              onClick={() => setStep(5)}
            >
              Continuar
            </button>
          </div>
        </div>
      )}

      {/* Paso 5: Beneficiario */}
      {/* Paso 5: Beneficiario o entrega en efectivo */}
      {step === 5 && (
        <div className="step-content">
          {deliveryOption === "transferencia" || deliveryOption === "Zelle" ? (
            <>
              <h2>Selecciona el beneficiario</h2>
              {directory.filter((b) => b.accbsUser_country === selectedCountry)
                .length === 0 ? (
                <div className="empty-beneficiaries">
                  <p>No tienes beneficiarios en este país.</p>
                  <Link className="add-beneficiary" to="/directory">
                    Agregar Beneficiario
                  </Link>
                </div>
              ) : (
                <>
                  <div className="recipient-list">
                    {directory
                      .filter((b) => b.accbsUser_country === selectedCountry)
                      .map((b) => (
                        <div
                          key={b.accbsUser_id}
                          className="recipient-card"
                          onClick={() => {
                            setSelectedBeneficiary(b);
                            setStep(6);
                          }}
                        >
                          <img
                            src={`https://flagcdn.com/w40/${
                              countryFlags[b.accbsUser_country]
                            }.png`}
                            alt={b.accbsUser_country}
                            className="flag-icon"
                          />
                          <div className="info">
                            <h4>{b.accbsUser_owner}</h4>
                            <p>
                              {b.accbsUser_bank} -{" "}
                              {b.accbsUser_type === "Pago Móvil"
                                ? b.accbsUser_phone
                                : b.accbsUser_number}
                            </p>
                          </div>
                        </div>
                      ))}
                  </div>
                  <div className="actions">
                    <Link className="add-beneficiary" to="/directory">
                      + Agregar Beneficiario
                    </Link>
                  </div>
                </>
              )}
              <div className="actions">
                <button onClick={() => setStep(4)}>Atrás</button>
              </div>
            </>
          ) : (
            <>
              <h2>Datos de entrega en efectivo</h2>

              {/* Selección de ubicación */}
              <div className="form-group">
                <label>Ubicación</label>
                <select
                  defaultValue=""
                  onChange={(e) => fetchDataPorcentId(e.target.value)}
                >
                  <option value="">Seleccione una ubicación</option>
                  {porcents.map((por) => {
                    if (por.por_status === "Desactivado") return null;
                    return (
                      <option key={por.por_id} value={por.por_id}>
                        {por.por_stateLocation}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Mostrar información solo si ya se seleccionó ubicación */}
              {porcent?.por_id && (
                <>
                  {(() => {
                    // 🧮 Calculamos el monto a debitar y el saldo disponible real
                    const montoADebitar = calculateValue(
                      amount,
                      payment,
                      porcent
                    );
                    const saldoDisponible =
                      payment === "EUR"
                        ? user.use_amountEur
                        : payment === "USD"
                        ? user.use_amountUsd
                        : user.use_amountGbp;

                    const saldoInsuficiente = montoADebitar > saldoDisponible;

                    return (
                      <>
                        <div className="sendmoney-info">
                          <div className="form-group">
                            <p>
                              <strong>Porcentaje:</strong> {getPercentage()}%
                            </p>
                            <p>
                              <strong>Estado:</strong> {porcent.por_status}
                            </p>

                            {porcent.por_status === "Oficina" && (
                              <p>
                                <strong>Comentario:</strong>{" "}
                                {porcent.por_comment}
                              </p>
                            )}

                            {porcent.por_status === "Obligatorio" && (
                              <p>
                                <strong>Precio de entrega:</strong>{" "}
                                {porcent.por_deliveryPrice} {payment}
                              </p>
                            )}

                            <hr />

                            <p>
                              <strong>Monto a debitar de la billetera:</strong>{" "}
                              {montoADebitar.toFixed(2)} {payment}
                            </p>
                            <p>
                              <strong>Monto a recibir:</strong> {amount} USD
                            </p>
                            <div className="wallet-check success">
                              Saldo disponible: {saldoDisponible.toFixed(2)}{" "}
                              {payment}
                            </div>

                            {saldoInsuficiente && (
                              <div className="balance-warning">
                                ⚠️ Saldo insuficiente: tienes{" "}
                                <strong>
                                  {saldoDisponible.toFixed(2)} {payment}
                                </strong>{" "}
                                y necesitas{" "}
                                <strong>
                                  {montoADebitar.toFixed(2)} {payment}
                                </strong>
                                .
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Datos del receptor */}
                        <div className="form-group">
                          <label htmlFor="receiver-name">
                            Nombre de quien recibe
                          </label>
                          <input
                            type="text"
                            id="receiver-name"
                            value={receiverName}
                            onChange={(e) => setReceiverName(e.target.value)}
                            placeholder="Ingrese el nombre del receptor"
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="receiver-dni">
                            Cédula de quien recibe
                          </label>
                          <input
                            type="text"
                            id="receiver-dni"
                            value={receiverDni}
                            onChange={(e) => setReceiverDni(e.target.value)}
                            placeholder="Ingrese la cédula del receptor"
                          />
                        </div>

                        {porcent.por_status === "Obligatorio" && (
                          <>
                            <div className="form-group">
                              <label htmlFor="phone">
                                Número de contacto de quien recibe
                              </label>
                              <input
                                type="text"
                                id="phone"
                                value={receiverPhone}
                                onChange={(e) =>
                                  setReceiverPhone(e.target.value)
                                }
                                placeholder="Ingrese número de contacto"
                              />
                            </div>

                            <div className="form-group">
                              <label htmlFor="delivery-address">
                                Dirección de entrega
                              </label>
                              <input
                                type="text"
                                id="delivery-address"
                                value={receiverAddress}
                                onChange={(e) =>
                                  setReceiverAddress(e.target.value)
                                }
                                placeholder="Ingrese la dirección de entrega"
                              />
                            </div>
                          </>
                        )}

                        {porcent.por_status === "Oficina" && (
                          <div className="form-group">
                            <label>Dirección de la oficina</label>
                            <input
                              type="text"
                              value={porcent.por_comment}
                              disabled
                            />
                          </div>
                        )}

                        <div className="actions">
                          <button onClick={() => setStep(4)}>Atrás</button>
                          <button
                            disabled={
                              !receiverName ||
                              !receiverDni ||
                              (porcent.por_status === "Obligatorio" &&
                                (!receiverPhone || !receiverAddress)) ||
                              saldoInsuficiente
                            }
                            onClick={() => setStep(6)}
                            className={saldoInsuficiente ? "disabled" : ""}
                          >
                            Continuar
                          </button>
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Paso 6: Confirmar datos */}
      {step === 6 && (
        <div className="step-content">
          <h2>Confirmar envío</h2>
          <div className="summary-box">
            <p>
              <strong>País:</strong> {selectedCountry}
            </p>

            {deliveryOption === "efectivo" ? (
              <>
                <p>
                  <strong>Nombre del receptor:</strong> {receiverName}
                </p>
                <p>
                  <strong>Cédula del receptor:</strong> {receiverDni}
                </p>
                <p>
                  <strong>Teléfono del receptor:</strong> {receiverPhone}
                </p>
                <p>
                  <strong>Dirección de entrega:</strong> {receiverAddress}
                </p>
                <p>
                  <strong>Porcentaje:</strong> {getPercentage()}%
                </p>
                {porcent.por_status === "Obligatorio" && (
                  <p>
                    <strong>Precio de entrega:</strong>{" "}
                    {porcent.por_deliveryPrice} {payment}
                  </p>
                )}
                <p>
                  <strong>Monto a debitar de la billetera:</strong>{" "}
                  {calculateValue(amount, payment, porcent).toFixed(2)}{" "}
                  {payment}
                </p>
                <p>
                  <strong>Monto a recibir:</strong> {amount} {payment}
                </p>
              </>
            ) : (
              <>
                <p>
                  <strong>Monto:</strong> {amount} {payment}
                </p>
                <p>
                  <strong>Tasa:</strong>{" "}
                  {user.use_loyaltyEurtoBs > 0
                    ? `${user.use_loyaltyEurtoBs} EUR`
                    : (() => {
                        const rate = exchangeRates.find(
                          (r) =>
                            r.country === selectedCountry &&
                            r.oldCurrency === payment
                        );
                        return rate ? `${rate.value} ${rate.currency}` : "N/A";
                      })()}
                </p>
                <p>
                  <strong>Monto destino:</strong>{" "}
                  {(
                    parseFloat(amount) *
                    (user.use_loyaltyEurtoBs > 0
                      ? user.use_loyaltyEurtoBs
                      : exchangeRates.find(
                          (rate) =>
                            rate.country === selectedCountry &&
                            rate.oldCurrency === payment
                        )?.value || 0)
                  ).toLocaleString()}{" "}
                  <span>
                    {exchangeRates.find(
                      (rate) =>
                        rate.country === selectedCountry &&
                        rate.oldCurrency === payment
                    )?.currency || "N/A"}
                  </span>
                </p>
                <p>
                  <strong>Entrega:</strong> {deliveryOption}
                </p>
                {(deliveryOption === "transferencia" ||
                  deliveryOption === "Zelle") &&
                  selectedBeneficiary && (
                    <p>
                      <strong>Beneficiario:</strong>{" "}
                      {selectedBeneficiary.accbsUser_owner}
                    </p>
                  )}
              </>
            )}
          </div>

          <div className="actions">
            <button onClick={() => setStep(5)}>Atrás</button>
            <button
              onClick={() => {
                setStep(7);
                handleSend();
              }}
            >
              Confirmar envío
            </button>
          </div>
        </div>
      )}

      {/* Paso 7: Resultado */}
      {step === 7 && (
        <div className="step-content">
          {loading ? (
            <div className="step-loader">
              <div className="spinner"></div>
              <p>Procesando transacción...</p>
            </div>
          ) : transactionSuccess ? (
            <>
              <FaCheckCircle
                size={64}
                color="#00a896"
                className="status-icon animate-pop"
              />
              <h2>¡Envío exitoso!</h2>
              <p>Tu transacción fue procesada correctamente.</p>
            </>
          ) : (
            <>
              <FaTimesCircle
                size={64}
                color="#e63946"
                className="status-icon animate-pop"
              />
              <h2>Error</h2>
              <p>Ocurrió un problema, intenta nuevamente.</p>
            </>
          )}

          {/* Oculta botones si está cargando */}
          {!loading && (
            <div className="actions">
              <button onClick={() => history.push("/changes")}>
                Finalizar
              </button>
              <button onClick={() => window.location.reload()}>
                Nueva transacción
              </button>
            </div>
          )}
        </div>
      )}

      <UserNavbar logOut={user?.use_email} />
    </div>
  );
}

export { SendMoney };
